import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useEffect, useRef, useMemo, useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { PUBLIC_ROUTES } from "@/routes/public";
import { useTranslation } from "@/hooks/useTranslation";
import { commonI18n } from "@/utils/i18n";
import { useAuth } from "@/hooks/useAuth";
import { useHMRStableRef } from "@/hooks/useHMRStable";
import { useSocialLoginCallback } from "@/hooks/useSocialLoginCallback";
import { useLoginStore, type LoginStore } from "@/stores/loginStore";
import { AUTH_REDIRECT_REASON_KEY, AUTH_REDIRECT_REASON_SESSION_EXPIRED } from "@/services/api/client";
import { resendVerificationEmail } from "@/services/api/auth";
import { LoginHeader } from "@/components/login/LoginHeader";
import { LoginPageContent } from "@/components/login/LoginPageContent";

/**
 * Only accept same-origin relative paths for post-login redirects (JUB-76).
 * Rejects absolute URLs, protocol-relative URLs ("//evil.com") and
 * backslash tricks ("/\evil.com") to avoid open redirects.
 */
function sanitizeReturnTo(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//')) return null;
  if (raw.includes('\\')) return null;
  return raw;
}

type LoginFormData = {
  user_email: string;
  user_password: string;
};

export default function LoginPage() {
  const { t, currentLanguage } = useTranslation();
  const localeCode = currentLanguage as 'ro' | 'en';
  const locale = useRouteLocale();
  const navigate = useNavigate();
  const location = useLocation();

  usePageMeta({
    title: t("auth.login.metaTitle"),
    description: t("auth.login.metaDescription"),
    locale: locale,
    path: PUBLIC_ROUTES.login(locale),
    alternates: { ro: PUBLIC_ROUTES.login("ro"), en: PUBLIC_ROUTES.login("en") },
  });
  const { login: handleLogin, loading } = useAuth();
  const currentLocale = locale || 'ro';
  
  // Handle social login callback (extracted to hook for easier testing)
  useSocialLoginCallback();
  
  // Use Zustand selectors to prevent unnecessary re-renders
  // Use separate selectors - this is safer than returning an object from selector
  // (which causes infinite loops because it creates a new object reference each time)
  const loginError = useLoginStore((state: LoginStore) => state.error);
  const setLoginError = useLoginStore((state: LoginStore) => state.setError);
  const clearLoginError = useLoginStore((state: LoginStore) => state.clearError);
  
  const isSubmittingRef = useRef(false);
  const justSetErrorRef = useHMRStableRef<boolean>('login-just-set-error', false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendingVerification, setResendingVerification] = useState(false);

  // Where to go after a successful login (set by the 401 session-expired
  // redirect in the API client). Validated against open redirects.
  const returnTo = useMemo(
    () => sanitizeReturnTo(new URLSearchParams(location.search).get('returnTo')),
    [location.search]
  );

  // Show the "session expired" message when the API client redirected us here
  // after a failed token refresh (JUB-76). The toast is deferred one tick so the
  // global <Toaster /> (whose effect runs after this child effect) is subscribed;
  // a toast dispatched synchronously here would be silently dropped by sonner.
  useEffect(() => {
    if (sessionStorage.getItem(AUTH_REDIRECT_REASON_KEY) === AUTH_REDIRECT_REASON_SESSION_EXPIRED) {
      sessionStorage.removeItem(AUTH_REDIRECT_REASON_KEY);
      const message = t('auth.sessionExpired');
      // Persistent inline message in the login form...
      setLoginError(message);
      // ...plus the toast (deliberately no cleanup: clearing the timer under
      // StrictMode's double-invoke would swallow the toast).
      setTimeout(() => toast.error(message), 0);
    }
  }, [t, setLoginError]);
  
  // Memoize schema to prevent recreation on every render
  const loginSchema = useMemo(() => z.object({
    user_email: z.string().email(t('validation.emailInvalid')),
    user_password: z.string().min(1, t('validation.passwordRequired')),
  }), [t]);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Clear error when leaving OR arriving at the login page
  // This ensures errors don't persist across navigation
  const previousPathRef = useHMRStableRef<string | null>('login-page-previous-path', null);
  
  useEffect(() => {
    const current = location.pathname;
    const previous = previousPathRef.current;
    const isLoginPage = current.endsWith('/login');
    const wasLoginPage = previous?.endsWith('/login') ?? false;
    
    // Clear error if:
    // 1. We're leaving the login page, OR
    // 2. We're arriving at the login page from a different route (navigation back)
    if ((wasLoginPage && !isLoginPage) || (!wasLoginPage && isLoginPage && previous !== null)) {
      clearLoginError();
      justSetErrorRef.current = false; // Reset guards
    }
    
    // Update previous path
    previousPathRef.current = current;
  }, [location.pathname, clearLoginError]);

  const onSubmit = useCallback(async (data: LoginFormData) => {
    isSubmittingRef.current = true;
    // DO NOT CLEAR AT BEGINNING OF SUBMIT - let the error persist until user interacts
    
    try {
      // Honor validated returnTo (session-expired flow) after successful login.
      await handleLogin(
        data.user_email,
        data.user_password,
        returnTo ? { redirectTo: returnTo } : undefined
      );
      // If successful, error will be cleared and user will be redirected
      clearLoginError(); // Clear error on success
      isSubmittingRef.current = false;
    } catch (error: unknown) {
      isSubmittingRef.current = false;

      // Extract error message from the error
      const axiosError = error as { response?: { data?: { error?: { message_key?: string; code?: string; message?: string; errors?: Record<string, string[]> }; message?: string } }; message?: string };
      const errorData = axiosError.response?.data;
      let errorMessage = t('auth.invalidCredentials') || commonI18n.get('auth.invalidCredentials', localeCode);
      
      if (errorData) {
        // Check for error object (new format)
        if (errorData.error) {
          const errorKey = errorData.error.message_key || errorData.error.code || 'invalid_credentials';
          if (errorKey === 'account_not_active') {
            setUnverifiedEmail(data.user_email);
          } else {
            setUnverifiedEmail(null);
          }
          // Try to get translated error message
          const translatedError = t(`errors.${errorKey}`);
          // If translation exists and is different from the key, use it
          if (translatedError && translatedError !== `errors.${errorKey}`) {
            errorMessage = translatedError;
          } else if (errorData.error.message) {
            errorMessage = errorData.error.message;
          }
          
          // Handle validation errors
          if (errorData.error.errors && Object.keys(errorData.error.errors).length > 0) {
            const firstError = Object.values(errorData.error.errors)[0] as string[];
            if (firstError && firstError.length > 0) {
              errorMessage = firstError[0];
            }
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (axiosError.message) {
        errorMessage = axiosError.message;
      }
      
      // Mark that we just set an error to prevent it from being cleared immediately
      justSetErrorRef.current = true;
      setTimeout(() => {
        justSetErrorRef.current = false;
      }, 3000); // Prevent clearing for 3 seconds
      
      // Set error state to display in form using Zustand store
      setLoginError(errorMessage);
      
      // Also show toast for visibility
      toast.error(errorMessage);
      
      // Don't re-throw - just set the error and stop
      // React Hook Form will handle the form state
      return;
    }
  }, [handleLogin, clearLoginError, setLoginError, t, localeCode, returnTo]);

  const handleFocus = useCallback(() => {
    // Check error directly from store to avoid stale closures
    const err = useLoginStore.getState().error;
    if (err && !isSubmittingRef.current && !justSetErrorRef.current) {
      clearLoginError();
    }
  }, [clearLoginError]);

  const handleGoToRegister = useCallback(() => {
    navigate(`/${currentLocale}/register`);
  }, [navigate, currentLocale]);

  const handleResendVerification = useCallback(async () => {
    if (!unverifiedEmail) return;
    try {
      setResendingVerification(true);
      await resendVerificationEmail(unverifiedEmail);
      toast.success(t('auth.emailVerification.resendSuccess'));
    } catch {
      toast.error(t('common.error'));
    } finally {
      setResendingVerification(false);
    }
  }, [unverifiedEmail, t]);

  return (
    <div className="min-h-screen bg-background">
      <LoginHeader 
        currentLocale={currentLocale}
        onGoToRegister={handleGoToRegister}
      />
      
      <LoginPageContent
        currentLocale={currentLocale}
        register={register}
        handleSubmit={handleSubmit}
        errors={errors}
        isSubmitting={isSubmitting}
        loading={loading}
        loginError={loginError}
        unverifiedEmail={unverifiedEmail}
        resendingVerification={resendingVerification}
        onResendVerification={handleResendVerification}
        onSubmit={onSubmit}
        onFocus={handleFocus}
        onGoToRegister={handleGoToRegister}
      />
    </div>
  );
}
